import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import WarehouseIcon from '@material-ui/icons/Store';
import { translator } from '../../utils/Translator';
import MainButton from "../UIElements/MainButton";

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root}>
      <Typography variant="h6">{children}</Typography>
      <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const DialogCheckStock = ({ open, product, onClose }) => {
  const { authUser } = useSelector(({ auth }) => auth);
  const { languageSelected } = useSelector(({ settings }) => settings);
  const { translate } = useSelector(({ i18n }) => i18n);

  const [loading, setLoading] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [totalAvaiQty, setTotalAvaiQty] = useState(0);

  useEffect(() => {
    const fetchStockData = async () => {
      if (!open || !product) return;

      setLoading(true);
      try {
        const params = {
          company: authUser.company !== "#" ? authUser.company : "",
          salesmanCode: authUser.salesman !== "#" ? authUser.salesman : "",
          productCode: product.ProductCode,
          language: languageSelected.id,
        };

        const url = `${process.env.REACT_APP_API_URL}availableStockTransaction/byProduct`;
        const config = {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('x-access-token')}`,
          },
        };

        const response = await axios.get(url, config);
        console.log("📦 Stock Data:", response.data);

        if (response.data.success) {
          setStockData(response.data.data || []);
          setTotalAvaiQty(response.data.totalAvaiQty || 0);
        }
      } catch (error) {
        console.error('Error fetching stock data:', error);
        setStockData([]);
        setTotalAvaiQty(0);
      } finally {
        setLoading(false);
      }
    };

    fetchStockData();
  }, [open, product, authUser.company, languageSelected.id]);

  const getLocationTypeLabel = (type) => {
    switch (type) {
      case 'NOR':
        return translator(translate.ISA_WarehouseGood, 'คลังดี');
      case 'RET':
        return translator(translate.ISA_WarehouseBad, 'คลังเสีย');
      default:
        return type;
    }
  };

  const getLocationTypeColor = (type) => {
    switch (type) {
      case 'NOR':
        return 'primary';
      case 'RET':
        return 'secondary';
      case 'DAM':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
    >
      <DialogTitle onClose={onClose}>
        <Box display="flex" alignItems="center">
          <WarehouseIcon style={{ marginRight: 8, color: "#ff6f00" }} />
          {translator(translate.ISA_CheckStock, "ตรวจสอบสต็อก")}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Product Info */}
        {product && (
          <Box mb={3} p={2} bgcolor="#f5f5f5" borderRadius={2}>
            <Typography variant="h6" gutterBottom>
              {product.ProductName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {translator(translate.ISA_ProductCode, "รหัสสินค้า")}: {product.ProductCode}
            </Typography>
          </Box>
        )}

        {/* Loading */}
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress style={{ color: "#ff6f00" }} />
          </Box>
        )}

        {/* Stock Table */}
        {!loading && stockData.length > 0 && (
          <TableContainer component={Paper} elevation={0} style={{ border: '1px solid #e0e0e0' }}>
            <Table>
              <TableHead style={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell align="center" style={{ fontWeight: 'bold' }}>{translator(translate.ISA_Sequence, "ลำดับ")}</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>{translator(translate.ISA_Warehouse, "โกดัง")}</TableCell>
                  <TableCell align="center" style={{ fontWeight: 'bold' }}>{translator(translate.ISA_Type, "ประเภท")}</TableCell>
                  <TableCell align="center" style={{ fontWeight: 'bold' }}>{translator(translate.ISA_LotNo, "LotNo")}</TableCell>
                  <TableCell align="center" style={{ fontWeight: 'bold' }}>{translator(translate.ISA_ManufactureDate, "วันผลิต")}</TableCell>
                  <TableCell align="center" style={{ fontWeight: 'bold' }}>{translator(translate.ISA_Lot, "วันหมดอายุ")}</TableCell>
                  <TableCell align="right" style={{ fontWeight: 'bold' }}>{translator(translate.ISA_Qty, "จำนวน")}</TableCell>
                  <TableCell align="center" style={{ fontWeight: 'bold' }}>{translator(translate.ISA_Unit, "หน่วย")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stockData.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell align="center">{index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {item.PlantName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ({item.Plant})
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={getLocationTypeLabel(item.LocationType)}
                        color={getLocationTypeColor(item.LocationType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {item.LotNo || '-'}
                    </TableCell>
                    <TableCell align="center">
                      {item.MFGDate || '-'}
                    </TableCell>
                    <TableCell align="center">
                      {item.EXPDate || '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" style={{ fontWeight: 'bold', color: '#ff6f00' }}>
                        {item.BalanceQtyDisplay.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{item.Unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* No Data */}
        {!loading && stockData.length === 0 && (
          <Box p={4} textAlign="center">
            <Typography variant="body1" color="textSecondary">
              {translator(translate.ISA_NoStockDataFound, "ไม่พบข้อมูลสต็อก")}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <MainButton
          label={translator(translate.ISA_Status_Closed, "ปิด")}
          handleClick={onClose}
        />
      </DialogActions>
    </Dialog>
  );
};

export default DialogCheckStock;
